import { handleActions } from 'redux-actions';
import { push } from "react-router-redux";
import { initialize as initializeForm } from 'redux-form';
import { NotificationManager } from "react-notifications";
import { api } from "api";
import Swal from 'sweetalert2';
import { Router } from 'react-router';

const SUBMIT = 'LOGIN_SUBMIT';
const LOADER = 'LOGIN_LOADER';
const ME = 'LOGIN_ME';
const MUST_CHANGE_PASSWORD = 'MUST_CHANGE_PASSWORD';
const EMAIL_SENT = "EMAIL_SENT";

export const constants = {
    SUBMIT,
};

// ------------------------------------
// Pure Actions
// ------------------------------------

export const setLoader = loader => ({
    type: LOADER,
    loader,
});

export const setMe = me => ({
    type: ME,
    me,
});

export const setMustChangePassword = mustChange => ({
    type: MUST_CHANGE_PASSWORD,
    mustChange,
});


const handleError = (error, defaultMsg = "Error") => {
    console.error(error);
    Swal.fire({
        icon: 'error',
        title: "ERROR",
        text: error ? error instanceof (Object) ? error.detail : error : ''
    })
}

// ------------------------------------
// Actions
// ------------------------------------

export const onSubmit = (data = {}) => (dispatch, getStore) => {
    dispatch(setLoader(true));
    api.post('user/token', data).then((response) => {
        localStorage.setItem('token', response.token);
        dispatch(initializeForm('profile', response.user));
        dispatch(setMe(response.user));

        if (response.must_change_password) {
            dispatch(setMustChangePassword(true));
        } else {
            dispatch(push("/"));
        }
    }).catch((e) => {
        console.error("Login error: ", e);
        if (e && e.detail) {
            NotificationManager.error(e.detail, 'ERROR', 0);
        } else if (e && e.message) {
            NotificationManager.error(e.message, 'ERROR', 0);
        } else {
            NotificationManager.error('Error al iniciar sesión', 'ERROR', 0);
        }
    }).finally(() => {
        dispatch(setLoader(false));
    });
};

export const onChangePasswordFirstLogin = (data) => (dispatch) => {
    dispatch(setLoader(true));
    api.post('user/change_password_first_login', data).then(() => {
        Swal.fire('Éxito', 'Contraseña actualizada correctamente', 'success').then(() => {
            dispatch(setMustChangePassword(false));
            dispatch(push("/"));
        });
    }).catch((e) => {
        handleError(e);
    }).finally(() => {
        dispatch(setLoader(false));
    });
};

export const getMe = () => (dispatch) => {
    api.get('/user/me').then(me => {
        dispatch(initializeForm('profile', me));
        dispatch(setMe(me));
    })
        .catch((e) => {
            localStorage.removeItem('token');
            dispatch(push('/user/logout'))
            const errorMsg = (e && e.body && e.body.detail) || "La sesión ha expirado o no es válida";
            NotificationManager.error(errorMsg, 'ERROR', 0);
        }).finally(() => { });
};

export const refreshPermissions = () => (dispatch, getStore) => {
    // Silently refresh permissions without showing loaders or errors to user
    console.log('[Permission Refresh] Checking for permission updates...');

    api.get('/user/refresh_permissions').then(response => {
        const currentMe = getStore().login.me;

        // Check if role, role_configuration, or permissions changed
        const roleChanged = currentMe.role !== response.role;
        const roleConfigChanged = (currentMe.role_configuration ? currentMe.role_configuration.id : null) !== response.role_configuration_id;
        const permissionsChanged = JSON.stringify(currentMe.permissions) !== JSON.stringify(response.permissions);

        if (roleChanged || roleConfigChanged || permissionsChanged) {
            console.log('[Permission Refresh] Changes detected — fetching full me data');

            // Fetch /user/me to get updated name_role and all fields
            api.get('/user/me').then(me => {
                dispatch(setMe(me));
                dispatch(initializeForm('profile', me));  // keep Redux Form in sync
                if (roleChanged) {
                    NotificationManager.info('Tu rol ha sido actualizado. Recarga si ves información desactualizada.', 'Actualización', 5000);
                } else if (permissionsChanged) {
                    NotificationManager.info('Tus permisos han sido actualizados', 'Actualización', 3000);
                }
            }).catch(() => {
                // Fallback: update with partial data if /me fails
                const updatedMe = {
                    ...currentMe,
                    permissions: response.permissions,
                    base_permissions: response.base_permissions,
                    role: response.role,
                    role_configuration: response.role_configuration_id ? {
                        ...(currentMe.role_configuration || {}),
                        id: response.role_configuration_id,
                        name: response.role_configuration_name
                    } : null
                };
                dispatch(setMe(updatedMe));
                dispatch(initializeForm('profile', updatedMe));  // keep Redux Form in sync
            });
        } else {
            console.log('[Permission Refresh] No changes detected');
        }
    })
        .catch((e) => {
            // Silently fail - don't disrupt user experience
            console.error('[Permission Refresh] Failed:', e);
        });
};


export const logOut = () => (dispatch) => {
    api.post('/user/logout').then(() => {
    }).catch(() => {
    }).finally(() => { });
    localStorage.removeItem('token');
};

export const onSubmitForgot = (data, again = false) => (dispatch, getStore) => {
    dispatch(setLoader(true));
    api.post('/user/reset_password', data).then(response => {
        dispatch({ type: EMAIL_SENT, emailSent: true });
        if (again) {
            Swal.fire({
                icon: "success",
                text: "Se volvio a enviar el correo"
            })
        } else {
            Swal.fire({
                icon: "success",
                text: "Se envio el correo"
            })
        }
    }).catch(error => {
        handleError(error, 'No se pudo enviar el correo');
    }).finally(() => {
        dispatch(setLoader(false))
    })
}

export const onSubmitRecover = (data) => (dispatch, getStore) => {
    dispatch(setLoader(true));
    api.post('/user/recover_password', data).then(response => {
        Swal.fire({
            icon: 'success',
            text: "La contraseña fue cambiada"
        }).then(() => {
            dispatch(push('/login'));
        })
    }).catch((error) => {
        handleError(error, 'No se pudo cambiar la contraseña');
    }).finally(() => {
        dispatch(setLoader(false));
    })
}


export const actions = {
    onSubmitForgot,
    onSubmitRecover,
    onSubmit,
    logOut,
    getMe,
    refreshPermissions,
    onChangePasswordFirstLogin,
    setMustChangePassword,
};

export const reducers = {
    [LOADER]: (state, { loader }) => ({ ...state, loader }),
    [ME]: (state, { me }) => {
        if (me && me.role && typeof me.role === 'object') {
            me.role = me.role.role;
        }
        return { ...state, me };
    },
    [EMAIL_SENT]: (state, { emailSent }) => ({ ...state, emailSent }),
    [MUST_CHANGE_PASSWORD]: (state, { mustChange }) => ({ ...state, mustChange }),
};

export const initialState = {
    loader: false,
    emailSent: false,
    me: {},
    mustChange: false,
};

export default handleActions(reducers, initialState);
